import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "@/App";
import { CRMLayout } from "@/components/CRMLayout";
import { toast } from "sonner";
import { Plus, TrendingUp, FileCheck2, Trophy, Loader2, X, Inbox, UserPlus, Archive } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";

const API_URL = process.env.REACT_APP_BACKEND_URL || "";

export const STATUS_COLUMNS = [
  { key: "new", label: "Nouveau", dot: "#0EA5E9" },
  { key: "contacted", label: "Contacté", dot: "#6366F1" },
  { key: "qualified", label: "Qualifié", dot: "#8B5CF6" },
  { key: "proposal", label: "Proposition", dot: "#F59E0B" },
  { key: "negotiation", label: "Négociation", dot: "#EC4899" },
  { key: "won", label: "Gagné", dot: "#16A34A" },
  { key: "lost", label: "Perdu", dot: "#94A3B8" },
];

export const fmtEUR = (n) =>
  (n || 0).toLocaleString("fr-FR", { maximumFractionDigits: 0 }) + " €";

export default function OpportunitiesPage() {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [opps, setOpps] = useState([]);
  const [stats, setStats] = useState(null);
  const [messages, setMessages] = useState([]);
  const [traitement, setTraitement] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    company_name: "", contact_name: "", contact_email: "",
    contact_phone: "", value: "", source: "",
  });

  const auth = { headers: { Authorization: `Bearer ${token}` } };

  const load = async () => {
    try {
      const [o, s, m] = await Promise.all([
        axios.get(`${API_URL}/api/opportunities`, auth),
        axios.get(`${API_URL}/api/opportunities/stats`, auth),
        axios.get(`${API_URL}/api/inbox`, auth).catch(() => ({ data: [] })),
      ]);
      setOpps(o.data);
      setStats(s.data);
      setMessages(m.data || []);
    } catch (e) {
      toast.error("Impossible de charger les opportunités");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  // Un message du formulaire de contact devient une opportunité en un clic.
  const convertirMessage = async (id) => {
    setTraitement(id);
    try {
      const { data } = await axios.post(`${API_URL}/api/inbox/${id}/convert`, {}, auth);
      toast.success(data.created ? "Opportunité créée" : "Message rattaché à l'opportunité existante");
      navigate(`/dashboard/opportunities/${data.opportunity_id}`);
    } catch (e) {
      toast.error("Conversion impossible");
      setTraitement(null);
    }
  };

  const archiverMessage = async (id) => {
    setTraitement(id);
    try {
      await axios.post(`${API_URL}/api/inbox/${id}/archive`, {}, auth);
      setMessages((liste) => liste.filter((m) => m.id !== id));
      toast.success("Message archivé");
    } catch (e) {
      toast.error("Archivage impossible");
    } finally {
      setTraitement(null);
    }
  };

  const createOpp = async () => {
    if (!form.company_name || !form.contact_name) {
      toast.error("Société et contact sont obligatoires");
      return;
    }
    setSaving(true);
    try {
      const { data } = await axios.post(`${API_URL}/api/opportunities`, {
        ...form, value: parseFloat(form.value) || 0,
      }, auth);
      toast.success("Opportunité créée");
      setShowNew(false);
      setForm({ company_name: "", contact_name: "", contact_email: "", contact_phone: "", value: "", source: "" });
      navigate(`/dashboard/opportunities/${data.id}`);
    } catch (e) {
      toast.error("Erreur lors de la création");
    } finally {
      setSaving(false);
    }
  };

  return (
    <CRMLayout>
      <div className="p-6 lg:p-8">
        {/* En-tête */}
        <div className="flex flex-wrap items-center gap-4 mb-6">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Opportunités</h1>
            <p className="text-slate-500 text-sm mt-0.5">
              Pipeline commercial — chaque fiche découverte alimente vos projets
            </p>
          </div>
          <div className="flex-1" />
          <Button onClick={() => setShowNew(true)} className="bg-indigo-600 hover:bg-indigo-700" data-testid="new-opportunity-btn">
            <Plus className="w-4 h-4 mr-1.5" /> Nouvelle opportunité
          </Button>
        </div>

        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
            <Card><CardContent className="p-4">
              <div className="text-[11px] font-bold uppercase tracking-wider text-slate-400">Pipeline ouvert</div>
              <div className="text-xl font-bold text-slate-900 mt-1 tabular-nums">{fmtEUR(stats.pipeline_value)}</div>
              <div className="text-xs text-slate-500">{stats.open_count} opportunité(s)</div>
            </CardContent></Card>
            <Card><CardContent className="p-4">
              <div className="text-[11px] font-bold uppercase tracking-wider text-slate-400">Fiches découverte</div>
              <div className="text-xl font-bold text-slate-900 mt-1 tabular-nums">{stats.forms_done} / {stats.total}</div>
              <div className="text-xs text-slate-500">complétées</div>
            </CardContent></Card>
            <Card><CardContent className="p-4">
              <div className="text-[11px] font-bold uppercase tracking-wider text-slate-400">Gagnées</div>
              <div className="text-xl font-bold text-emerald-600 mt-1 tabular-nums">{fmtEUR(stats.won_value)}</div>
              <div className="text-xs text-slate-500">{stats.won_count} signature(s)</div>
            </CardContent></Card>
            <Card><CardContent className="p-4">
              <div className="text-[11px] font-bold uppercase tracking-wider text-slate-400">Total</div>
              <div className="text-xl font-bold text-slate-900 mt-1 tabular-nums">{stats.total}</div>
              <div className="text-xs text-slate-500">opportunités suivies</div>
            </CardContent></Card>
          </div>
        )}

        {/* Boîte de réception — messages du formulaire de contact */}
        {messages.length > 0 && (
          <div className="bg-white border border-slate-200 rounded-xl shadow-sm mb-6 overflow-hidden">
            <div className="flex items-center gap-2.5 px-5 py-3 border-b border-slate-100 bg-slate-50/60">
              <Inbox className="w-4 h-4 text-indigo-600" />
              <h2 className="font-bold text-slate-900 text-sm">Boîte de réception</h2>
              <span className="text-[11px] font-semibold rounded-full bg-indigo-50 text-indigo-700 px-2 py-0.5">
                {messages.length}
              </span>
              <p className="text-[11px] text-slate-500 ml-1">
                Messages reçus via le formulaire de contact — à qualifier avant d'entrer au pipeline.
              </p>
            </div>
            <div className="divide-y divide-slate-100">
              {messages.map((m) => (
                <div key={m.id} className="flex flex-wrap items-start gap-3 px-5 py-3">
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-semibold text-slate-900">
                      {m.company || m.name}
                      {m.company && <span className="font-normal text-slate-500"> · {m.name}</span>}
                    </div>
                    <div className="text-xs text-slate-500">
                      {m.email} · {(m.created_at || "").slice(0, 10)}
                    </div>
                    <p className="text-sm text-slate-600 mt-1 line-clamp-2">{m.message}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button size="sm" onClick={() => convertirMessage(m.id)} disabled={traitement === m.id}
                      className="bg-indigo-600 hover:bg-indigo-700" data-testid={`inbox-convert-${m.id}`}>
                      {traitement === m.id
                        ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                        : <UserPlus className="w-3.5 h-3.5 mr-1.5" />}
                      Créer l'opportunité
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => archiverMessage(m.id)}
                      disabled={traitement === m.id}>
                      <Archive className="w-3.5 h-3.5 mr-1.5" /> Archiver
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Kanban */}
        {loading ? (
          <div className="flex items-center justify-center py-24 text-slate-400">
            <Loader2 className="w-6 h-6 animate-spin" />
          </div>
        ) : (
          <div className="flex gap-3 overflow-x-auto pb-4">
            {STATUS_COLUMNS.map((col) => {
              const items = opps.filter((o) => o.status === col.key);
              return (
                <div key={col.key} className="w-64 flex-shrink-0 bg-slate-100 rounded-xl p-2.5">
                  <div className="flex items-center gap-2 px-1.5 pb-2.5 text-xs font-bold uppercase tracking-wider text-slate-500">
                    <span className="w-2 h-2 rounded-full" style={{ background: col.dot }} />
                    {col.label}
                    <span className="ml-auto text-slate-400 tabular-nums">{items.length}</span>
                  </div>
                  <div className="flex flex-col gap-2 min-h-[60px]">
                    {items.map((o) => (
                      <button
                        key={o.id}
                        onClick={() => navigate(`/dashboard/opportunities/${o.id}`)}
                        className="text-left bg-white border border-slate-200 rounded-lg p-3 shadow-sm hover:border-indigo-500 hover:-translate-y-px transition"
                        data-testid={`opp-card-${o.id}`}
                      >
                        <div className="font-semibold text-sm text-slate-900">{o.company_name}</div>
                        <div className="text-xs text-slate-500 mt-0.5">{o.contact_name}</div>
                        <div className="flex items-center gap-1.5 mt-2.5">
                          <span className="font-bold text-[13px] tabular-nums text-slate-900">{fmtEUR(o.value)}</span>
                          {o.source && <span className="text-[11px] text-slate-400">· {o.source}</span>}
                          <span className={`ml-auto text-[10px] font-semibold rounded px-1.5 py-0.5 ${
                            o.discovery_form
                              ? "bg-emerald-50 text-emerald-600"
                              : "bg-amber-50 text-amber-600"
                          }`}>
                            {o.discovery_form ? "Fiche ✓" : "Fiche à faire"}
                          </span>
                        </div>
                        {/* Un prospect reste un prospect tant qu'aucun projet n'est ouvert. */}
                        <div className="flex flex-wrap items-center gap-1.5 mt-1.5">
                          {o.has_cdc && (
                            <span className="text-[10px] font-semibold rounded px-1.5 py-0.5 bg-indigo-50 text-indigo-600">
                              CDC rédigé
                            </span>
                          )}
                          {o.has_project ? (
                            <span className="text-[10px] font-semibold rounded px-1.5 py-0.5 bg-slate-900 text-white">
                              Projet ouvert
                            </span>
                          ) : (
                            <span className="text-[10px] font-semibold rounded px-1.5 py-0.5 bg-slate-100 text-slate-500">
                              Prospect
                            </span>
                          )}
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Modale création */}
        {showNew && (
          <div className="fixed inset-0 bg-slate-900/50 z-50 flex items-center justify-center p-4" onClick={() => setShowNew(false)}>
            <div className="bg-white rounded-xl shadow-xl w-full max-w-lg p-6" onClick={(e) => e.stopPropagation()}>
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-bold text-slate-900">Nouvelle opportunité</h2>
                <button onClick={() => setShowNew(false)} className="text-slate-400 hover:text-slate-600"><X className="w-5 h-5" /></button>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="sm:col-span-2">
                  <label className="text-xs font-semibold text-slate-700">Société *</label>
                  <Input value={form.company_name} onChange={(e) => setForm({ ...form, company_name: e.target.value })} placeholder="Ex : TP Océan Indien" />
                </div>
                <div>
                  <label className="text-xs font-semibold text-slate-700">Contact *</label>
                  <Input value={form.contact_name} onChange={(e) => setForm({ ...form, contact_name: e.target.value })} placeholder="Nom du contact" />
                </div>
                <div>
                  <label className="text-xs font-semibold text-slate-700">Téléphone</label>
                  <Input value={form.contact_phone} onChange={(e) => setForm({ ...form, contact_phone: e.target.value })} placeholder="0692 …" />
                </div>
                <div>
                  <label className="text-xs font-semibold text-slate-700">Email</label>
                  <Input type="email" value={form.contact_email} onChange={(e) => setForm({ ...form, contact_email: e.target.value })} placeholder="contact@…" />
                </div>
                <div>
                  <label className="text-xs font-semibold text-slate-700">Valeur estimée (€)</label>
                  <Input type="number" value={form.value} onChange={(e) => setForm({ ...form, value: e.target.value })} placeholder="10000" />
                </div>
                <div className="sm:col-span-2">
                  <label className="text-xs font-semibold text-slate-700">Source</label>
                  <Input value={form.source} onChange={(e) => setForm({ ...form, source: e.target.value })} placeholder="Site web, recommandation, salon…" />
                </div>
              </div>
              <div className="flex justify-end gap-2 mt-5">
                <Button variant="outline" onClick={() => setShowNew(false)}>Annuler</Button>
                <Button onClick={createOpp} disabled={saving} className="bg-indigo-600 hover:bg-indigo-700">
                  {saving && <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />} Créer
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </CRMLayout>
  );
}
